import { BadgeDollarSign, Check, FileText, MessageSquare, Paperclip, Phone, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { IconTile } from '@/components/reui/icon-tile';
import { normText, pasosParaTipo, type TipoPaso } from '@/components/rdt/rdt-domain';
import type { TipoRdt } from '@/types/api';

const ICONS: Record<TipoPaso, LucideIcon> = {
  chequeo: Zap,
  monto: BadgeDollarSign,
  expediente: FileText,
  hito: Check,
  contacto: Phone,
  adjunto: Paperclip,
  comentario: MessageSquare,
};

// Tono del IconTile (variant="soft", c-icon-tile-15: el tono sale de
// currentColor vía una sola clase de texto — mismo patrón que ICON_TINT de
// actividad.tsx, con "expediente" en vez de "referencia" (nombre del tipo
// de paso acá, no del ítem del hilo).
const ICON_TINT: Record<TipoPaso, string> = {
  chequeo: 'text-info-foreground dark:text-info',
  monto: 'text-success-foreground dark:text-success',
  expediente: 'text-warning-foreground dark:text-warning',
  hito: 'text-success-foreground dark:text-success',
  contacto: 'text-muted-foreground',
  adjunto: 'text-muted-foreground',
  comentario: 'text-text-tertiary',
};

interface Props {
  onSelect: (tipo: TipoPaso) => void;
  /** Filtra el catálogo por tipo (pasosParaTipo) — "Chequeo de índices" solo
   *  Provisorias, "Monto informado" solo Definitivas. */
  tipo: TipoRdt;
  /** Provisoria que YA saltó: "Chequeo de índices" queda visible pero
   *  deshabilitada (affordance de aprendizaje) — el ciclo de chequeos sigue
   *  en la RDT siguiente, no en esta. */
  yaSalto: boolean;
  /** Con el ciclo ya completo (Facturado, rdt.finalizada) se deshabilitan
   *  los pasos de GESTIÓN (hito/chequeo/monto) — contacto/adjunto/
   *  expediente/comentario siguen vivos (punto 1 de la validación v0.9.0).
   *  Opcional (default false) para no romper mientras el llamador
   *  (actividad.tsx) no lo cablea todavía. */
  finalizada?: boolean;
}

/** Motivo (título + hint) por el que una opción de la paleta está
 *  deshabilitada — generalización del flag antes hardcodeado a
 *  chequeo+yaSalto (punto 1): con el ciclo ya completo se suman
 *  hito/chequeo/monto (pasos de GESTIÓN); contacto/adjunto/expediente/
 *  comentario quedan siempre activos. */
function motivoDeshabilitado(key: TipoPaso, yaSalto: boolean, finalizada: boolean): { hint: string } | null {
  if (finalizada && (key === 'hito' || key === 'chequeo' || key === 'monto')) {
    return { hint: 'El ciclo ya está completo' };
  }
  if (key === 'chequeo' && yaSalto) {
    return { hint: 'esta Rede ya saltó — el ciclo sigue en la siguiente' };
  }
  return null;
}

// Mismo criterio de coincidencia que tenía el filtro a mano de antes
// (acento-insensible, label + sinónimo) pero como `filter` de cmdk — así el
// palmoteo de resultados, el conteo y el "Sin resultados" los maneja el
// propio Command, no un estado ni un querySelector nuestro (workflow.md
// §4.2: adaptar por reuso, no reinventar).
function filtrarPaso(value: string, search: string, keywords?: string[]): number {
  const s = normText(search);
  if (!s) return 1;
  const texto = normText([value, ...(keywords ?? [])].join(' '));
  return texto.includes(s) ? 1 : 0;
}

// Paleta de comandos "+ Paso": Command de ReUI/shadcn (c-command-3, grupos +
// buscador) — acá vive SOLO el contenido; el Popover que la abre/cierra,
// posiciona y devuelve el foco al botón «+ Paso» lo arma actividad.tsx
// (dueño del trigger), como pide el plan (§3 R3, «Command dentro de
// Popover»). Con el ciclo de vida ya completo se deshabilitan los pasos de
// gestión, con el hint puesto debajo de cada opción.
export function PasoPalette({ onSelect, tipo, yaSalto, finalizada = false }: Props) {
  const disponibles = pasosParaTipo(tipo);
  const grupos = [...new Set(disponibles.map(o => o.grupo))];

  return (
    <Command filter={filtrarPaso} loop label="Tipos de paso" className="rounded-xl!">
      <CommandInput placeholder="Buscar paso…" aria-label="Buscar tipo de paso" autoFocus />
      <CommandList className="max-h-[min(72vh,600px)]">
        <CommandEmpty>Sin resultados</CommandEmpty>
        {grupos.map(grupo => (
          <CommandGroup key={grupo} heading={grupo}>
            {disponibles
              .filter(o => o.grupo === grupo)
              .map(o => {
                const Icon = ICONS[o.key];
                const motivo = motivoDeshabilitado(o.key, yaSalto, finalizada);
                return (
                  <CommandItem
                    key={o.key}
                    value={o.label}
                    keywords={[o.kw]}
                    disabled={motivo !== null}
                    onSelect={() => onSelect(o.key)}
                    className="flex-col items-start gap-0.5"
                  >
                    <span className="flex w-full items-center gap-[9px]">
                      <IconTile variant="soft" size="xs" className={ICON_TINT[o.key]} aria-hidden="true">
                        <Icon strokeWidth={2.2} />
                      </IconTile>
                      {o.label}
                    </span>
                    {motivo && <span className="pl-[33px] text-[10.5px] font-normal italic text-text-tertiary">{motivo.hint}</span>}
                  </CommandItem>
                );
              })}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}
